// Helper for sanitising, testing and comparing Mobile Numbers
// Depends: libphonenumber-js
//
// Instansiate a phone number object:
//    let number = new PhoneNumber(numbertext)
//
// Methods include:
//       isMobile()        - Returns true if the string looks like a valid mobile number
//       isLocal()         - Returns true if number's country matches default country code
//       isPossibleNumber() - Returns true if it might be a number, and we think we know the type
//       compare(number)   - Compare two mobile numbers to see if they match
//
// Properties include:
//       possible          - Is possibly a number
//       valid             - Is a valid number
//       type              - Type of phone (string ID)
//       national          - Nicely format as National number
//       international     - Nicely format as International number
//       pretty            - Formats number based on National vs International vs Mobile
//       tidy              - Formats as E.164 format

const PhoneUtil = require('libphonenumber-js/max')

class PhoneNumber {
   static get country() { return 'AU' }
   static get statePrefix() { return '08' }

   constructor(number, country, prefix) {
      // Hide _number property
      Object.defineProperty(this, '_number', { enumerable: false, writable: true })

      // Add Enumerated Getters for properties mapped through to source object
      ;['possible', 'type', 'valid', 'country', 'phone', 'countryCallingCode'].forEach(prop => {
         // Redefine using existing Getter
         Object.defineProperty(this, prop, { enumerable: true, get: Object.getOwnPropertyDescriptor(PhoneNumber.prototype, prop).get })
      })

      // Clean up the number a bit first (get rid of symbols, etc)
      // if (!number || !number.match(/\d/) || number.match(/\w\+/)) return false // Blank or 0 entry? Nope.
      // number = number.replace(/[^\+\d]/g,'')

      // Defaults
      if (!country) country = PhoneNumber.country
      if (!prefix) prefix = PhoneNumber.statePrefix

      // Parse and save to object
      this._number = PhoneUtil.parsePhoneNumberFromString(number, country)
      // console.log(number, country, Object.assign({}, this))

      // Check for National numbers missing state prefix
      if (this.possible && !this.valid && this.country == country) {
         let trythis = PhoneUtil.parsePhoneNumberFromString(prefix + number, country)
         if (trythis.isValid()) {
            // console.log('Phone Number - Adding State Prefix:', trythis.formatInternational(), country)
            this._number = trythis
         }
      }

      // Check for International numbers missing + prefix
      if ((!this.possible || !this.valid) && number.match(/^[^\+]/)) {
         let trythis = PhoneUtil.parsePhoneNumberFromString('+' + number, country)
         if (trythis && trythis.isValid()) {
            // console.log('Phone Number - Adding + prefix:', trythis.formatInternational())
            this._number = trythis
         }
      }

      if (!this.possible) {
         // console.log('Not Possible', number, this, this.possible, this.type)
      }
      // console.log(this._number.number, this._number.isValid(), this.valid, this._number.getType(), this.type, this._number.country, this.country)
   }

   // Static short-hand methods
   static isLocal(number) { return number ? new PhoneNumber(number).isLocal() : false }
   static isPossibleNumber(number) { return number ? new PhoneNumber(number).isPossibleNumber() : false }
   static isMobile(number) {
      return number ? new PhoneNumber(number).isMobile() : false }
   static compare(num1, num2) { return num1 && num2 ? new PhoneNumber(num1).compare(new PhoneNumber(num2)) : num1 === num2 }

   // For backward compatibility
   static get region() { return PhoneNumber.country }

   get type() {
      return this._number ? this._number.getType() : undefined
   }

   get possible() { return this._number ? this._number.isPossible() : false }
   get valid() { return this._number ? this._number.isValid() : false }
   get country() { return this._number ? this._number.country : undefined }

   // Mainly for backward compatibility with libphonenumbe-js pre 1.6.0.
   get phone() { return this._number ? this._number.nationalNumber : false }

   isLocal() {
      return this.country == PhoneNumber.country
   }

   isPossibleNumber() {
      if (!this.possible)
         return false

      // Sometimes invalid possible numbers do have a type - sometimes they don't. Relevant?
      return !!this.type
   }

   isMobile()
   {
      // Note: "includes" is ES6! Use recent node.js, polyfill, or transpiler as needed
      return this.possible && this.type && this.type.includes('MOBILE') || false
   }

   compare(number) {
      if (!(number instanceof PhoneNumber))
         number = new PhoneNumber(number)

      // return this.countryCallingCode == number.countryCallingCode && this.phone == number.phone
      return this.value() == number.value()
   }

   get national() {
      return this.possible && this._number.formatNational() || ''
   }

   get international() {
      return this.possible && this._number.formatInternational() || ''
   }

   get countryCallingCode() {
      return this.possible && this._number.countryCallingCode || ''
   }

   get uri() {
      return this.possible && this._number.format('RFC3966') || ''
   }

   get pretty() {
      return this.possible ? this.isLocal() ? this.national : this.international : ''
   }

   get tidy() {
      return this.possible ? this._number.number : ''
   }

   toString() {
      return this.possible ? this._number.number : ''
   }

   value() {
      return this.possible ? this._number.number : ''
   }

   // Legacy "pretty" function
   static pretty(number) {
      number = new PhoneNumber(number)
      let tidy = number.tidy.replace(/^\+/,'')
      if (number.isMobile())
      {
         // International format Mobile Number
         var bits = tidy.match(/^(\d{2})(\d{1})(\d{4})(\d+)/)
         if (bits) {
            bits.shift()
            return '+' + bits.join(' ')
         }
      }
      else if (number.tidy.match(/^0/))
      {
         // National number with area code
         var bits = tidy.match(/^(\d{2})(\d{4})(\d+)/)
         if (bits) {
            bits.shift()
            return '(' + bits.shift() + ') ' + bits.join(' ')
         }
      }
      else
      {
         // Local number
         var bits = tidy.match(/^(\d{4})(\d+)/)
         if (bits) {
            bits.shift()
            return bits.join(' ')
         }
      }
      return tidy
   }
}

module.exports = PhoneNumber
